import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Badge,
  Card,
  Row,
  Col,
  Pagination,
  InputGroup,
} from "react-bootstrap";
import {
  FaEye,
  FaCheck,
  FaTimes,
  FaFilter,
  FaFileExcel,
  FaClock,
  FaSync,
  FaPlus,
} from "react-icons/fa";
import { gastosService } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";

const JornadaList = () => {
  const { user, isAdmin } = useAuth();
  const location = useLocation();
  const empleadoIdFromURL = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("empleado_id") || "";
  }, [location.search]);
  const [jornadas, setJornadas] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 8,
    total: 0,
    pages: 0,
  });

  // Estados para selección múltiple y validación
  const [selectedJornadas, setSelectedJornadas] = useState(new Set());
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showConfirmIndividualModal, setShowConfirmIndividualModal] = useState(false);
  const [jornadaToValidate, setJornadaToValidate] = useState(null);

  // Estado para modal de fichajes (turnos)
  const [selectedJornadaShifts, setSelectedJornadaShifts] = useState({
    jornada: null,
    loading: false,
    shifts: [],
    show: false,
    error: null,
  });

  // Formatear fecha a YYYY-MM-DD en horario local (evita desfases por UTC)
  const formatDateToLocalISO = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Función para obtener el primer día del mes actual y hoy (en local)
  const getCurrentMonthDates = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    // Ajuste: fecha hasta por defecto será HOY, no el último día del mes
    const lastDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    return {
      firstDay: formatDateToLocalISO(firstDay), // YYYY-MM-DD local
      lastDay: formatDateToLocalISO(lastDay),   // YYYY-MM-DD local
    };
  };

  // Filtros para jornadas con valores por defecto
  const [filters, setFilters] = useState({
    // Alinear con las opciones del selector: "1"=Todos, "2"=Sí, "0"=No
    validado: "0", // Por defecto: No validados
    empleado_id: empleadoIdFromURL,
    fecha_desde: getCurrentMonthDates().firstDay, // Primer día del mes actual
    fecha_hasta: getCurrentMonthDates().lastDay,  // Último día del mes actual
    orderBy: "0", // Fecha (Asc)
  });

  // Estado visual para los campos de fecha (no dispara consultas)
  const [dateInputs, setDateInputs] = useState({
    fecha_desde: getCurrentMonthDates().firstDay,
    fecha_hasta: getCurrentMonthDates().lastDay,
  });

  // Debug: Mostrar información del usuario y permisos
  console.log("JornadaList - Información del usuario:", {
    user: user,
    isAdmin: isAdmin,
    userRol: user?.ROL,
  });

  // Debounce para las fechas
  const debouncedDateChange = useCallback(
    (() => {
      let timeoutId;
      return (field, value) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          handleFilterChange(field, value);
        }, 1000); // 1 segundo de delay para fechas
      };
    })(),
    []
  );

  // Sincronizar inputs de fecha con filtros al montar y cuando cambien
  useEffect(() => {
    setDateInputs({
      fecha_desde: filters.fecha_desde,
      fecha_hasta: filters.fecha_hasta,
    });
  }, []);

  useEffect(() => {
    loadData();
  }, [pagination.page, filters]);

  // useEffect para cargar datos estáticos
  useEffect(() => {
    if (user) loadStaticData();
  }, [user]);

  // Inicializar los valores de fecha
  useEffect(() => {
    setDateInputs({
      fecha_desde: filters.fecha_desde,
      fecha_hasta: filters.fecha_hasta,
    });
  }, [filters.fecha_desde, filters.fecha_hasta]);

  // Aplicar cambios del empleado_id cuando cambie la URL
  useEffect(() => {
    if (empleadoIdFromURL) {
      setFilters((prev) => ({ ...prev, empleado_id: empleadoIdFromURL }));
    }
    // eslint-disable-next-line
  }, [empleadoIdFromURL]);

  const loadStaticData = async () => {
    try {
      setLoading(true);
      // Cargar empleados si es admin
      if (user && user.ROL === "admin") {
        const empleadosResponse = await gastosService.getEmpleados();
        setEmpleados(empleadosResponse.data);
      }
    } catch (error) {
      toast.error("Error al cargar datos estáticos");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const latestRequestRef = useRef(0);

  const loadData = async () => {
    const requestId = ++latestRequestRef.current;
    try {
      setTableLoading(true);

      // Cargar jornadas usando el stored procedure Q2B_OBTENER_LISTADO_JORNADAS
      const jornadasResponse = await gastosService.getJornadas({
        ...filters,
        page: pagination.page,
        limit: pagination.limit,
      });

      if (latestRequestRef.current !== requestId) return;

      setJornadas(jornadasResponse.data.jornadas);
      setPagination((prev) => ({
        ...prev,
        ...jornadasResponse.data.pagination,
      }));
    } catch (error) {
      if (latestRequestRef.current !== requestId) return;
      toast.error("Error al cargar jornadas");
      console.error("Error:", error);
    } finally {
      if (latestRequestRef.current === requestId) {
        setTableLoading(false);
      }
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Normalizadores de estado VALIDADO (el backend puede enviar 0/1 o booleanos)
  const isValidadoTrue = (value) => value === true || value === 1 || value === "1";
  const isValidadoFalse = (value) => value === false || value === 0 || value === "0";

  // Permitir validar solo cuando el estado sea Pendiente o No hay fichajes
  // (mismo orden de evaluación que getStatusBadge)
  const canValidateJornada = (jornada) => {
    if (isValidadoTrue(jornada.VALIDADO)) return false; // Validado
    if (isValidadoFalse(jornada.VALIDADO)) return true; // Pendiente
    if (jornada.HAY_EVENTO === 0) return false; // No hay evento
    if (jornada.VALIDADO === null) return true; // No hay fichajes
    return false; // Cualquier otro caso
  };

  const getStatusBadge = (validado, hayEvento) => {
    // Priorizar el estado VALIDADO por encima de "No hay evento"
    if (isValidadoTrue(validado)) {
      return <Badge bg="success">Validado</Badge>;
    }
    if (isValidadoFalse(validado)) {
      return <Badge bg="warning">Pendiente</Badge>;
    }
    if (hayEvento === 0) {
      return <Badge bg="secondary">No hay evento</Badge>;
    }
    if (validado === null) {
      return <Badge bg="secondary">No hay fichajes</Badge>;
    }
    return <Badge bg="secondary">-</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("es-ES");
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return (
      date.toLocaleDateString("es-ES") +
      " " +
      date.toLocaleTimeString("es-ES", {
        hour: "2-digit",
        minute: "2-digit",
      })
    );
  };

  const orderByOptions = [
    { value: 0, label: "Fecha (Desc)" },
    { value: 1, label: "Fecha (Asc)" },
    { value: 2, label: "Empleado (Asc)" },
    { value: 3, label: "Empleado (Desc)" },
    { value: 4, label: "Validado (Asc)" },
    { value: 5, label: "Validado (Desc)" },
  ];

  const handleVerFichajes = (jornada) => {
    setSelectedJornadaShifts({ jornada, loading: true, shifts: [], show: true, error: null });
    fetchShiftsForJornada(jornada);
  };

  const fetchShiftsForJornada = async (jornada) => {
    try {
      setSelectedJornadaShifts((prev) => ({ ...prev, loading: true, error: null }));
      const factorialId = jornada.FACTORIAL_ID;
      const date = formatDateToLocalISO(new Date(jornada.FECHA));
      const response = await gastosService.getJornadaShifts(factorialId, date);
      const shifts = response.data?.data || [];
      setSelectedJornadaShifts((prev) => ({ ...prev, loading: false, shifts }));
    } catch (error) {
      setSelectedJornadaShifts((prev) => ({
        ...prev,
        loading: false,
        error: error.response?.data?.message || "Error al obtener turnos",
      }));
    }
  };

  // Preseleccionar jornadas pendientes (no validadas o sin fichajes) con evento
  useEffect(() => {
    if (jornadas && jornadas.length > 0) {
      const pendientesKeys = jornadas
        .filter((j) => canValidateJornada(j))
        .map((j) => buildJornadaKey(j));
      setSelectedJornadas(new Set(pendientesKeys));
    } else {
      setSelectedJornadas(new Set());
    }
  }, [jornadas]);

  const handleValidarJornada = (jornada) => {
    setJornadaToValidate(jornada);
    setShowConfirmIndividualModal(true);
  };

  // Helpers para selección múltiple
  const buildJornadaKey = (j) => `${j.EMPLEADO_ID}_${j.FECHA}`;

  const handleSelectAll = (checked) => {
    if (checked) {
      const noValidadas = jornadas
        .filter((j) => canValidateJornada(j))
        .map((j) => buildJornadaKey(j));
      setSelectedJornadas(new Set(noValidadas));
    } else {
      setSelectedJornadas(new Set());
    }
  };

  const handleSelectJornada = (jornada, checked) => {
    const key = buildJornadaKey(jornada);
    const newSelected = new Set(selectedJornadas);
    if (checked) {
      newSelected.add(key);
    } else {
      newSelected.delete(key);
    }
    setSelectedJornadas(newSelected);
  };

  const handleRowClick = (jornada) => {
    if (canValidateJornada(jornada)) {
      const key = buildJornadaKey(jornada);
      const isSelected = selectedJornadas.has(key);
      handleSelectJornada(jornada, !isSelected);
    }
  };

  const getRowClassName = (jornada) => {
    let className = "";
    if (canValidateJornada(jornada)) {
      className += "table-danger ";
    }
    if (selectedJornadas.has(buildJornadaKey(jornada))) {
      className += "row-selected ";
    }
    return className.trim();
  };

  const handleValidarSeleccionados = async () => {
    try {
      setTableLoading(true);
      const seleccionadas = jornadas.filter((j) =>
        selectedJornadas.has(buildJornadaKey(j))
      );
      if (seleccionadas.length === 0) {
        toast.info("No hay jornadas seleccionadas");
        return;
      }
      // Intento de validación masiva vía backend (si no existe endpoint, se informará)
      const payload = seleccionadas.map((j) => ({
        empleadoId: j.EMPLEADO_ID,
        fecha: formatDateToLocalISO(new Date(j.FECHA)),
        validacionId: j.VALIDACION_JORNADA_ID,
      }));
      await gastosService.validarJornadas(payload);
      toast.success(`${seleccionadas.length} jornadas validadas exitosamente`);
      setSelectedJornadas(new Set());
      setShowConfirmModal(false);
      loadData();
      // Disparar refresco de contadores en navbar
      window.dispatchEvent(new Event("refreshPendingCounts"));
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message ||
          "No se pudo validar. Falta endpoint de validación de jornadas en backend"
      );
    } finally {
      setTableLoading(false);
    }
  };

  const handleValidarJornadaIndividual = async (jornada) => {
    try {
      setTableLoading(true);
      const payload = [{
        empleadoId: jornada.EMPLEADO_ID,
        fecha: formatDateToLocalISO(new Date(jornada.FECHA)),
        validacionId: jornada.VALIDACION_JORNADA_ID,
      }];
      await gastosService.validarJornadas(payload);
      toast.success("Jornada validada exitosamente");
      setShowConfirmIndividualModal(false);
      setJornadaToValidate(null);
      loadData();
      // Disparar refresco de contadores en navbar
      window.dispatchEvent(new Event("refreshPendingCounts"));
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message ||
          "No se pudo validar. Falta endpoint de validación de jornadas en backend"
      );
    } finally {
      setTableLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center mt-5">Cargando jornadas...</div>;
  }

  return (
    <div>
      {/* Filtros */}
      <Card className="filter-section mb-4">
        <Card.Header>
          <h6 className="mb-0">
            <FaFilter /> Filtros
          </h6>
        </Card.Header>
        <Card.Body>
          <Row>
            {user && user.ROL === "admin" && (
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Trabajador</Form.Label>
                  <Form.Select
                    value={filters.empleado_id}
                    onChange={(e) =>
                      handleFilterChange("empleado_id", e.target.value)
                    }
                  >
                    <option value="">Todos</option>
                    {empleados.map((empleado) => (
                      <option key={empleado.id} value={empleado.id}>
                        {empleado.nombre}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            )}
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Validado</Form.Label>
                <Form.Select
                  value={filters.validado}
                  onChange={(e) =>
                    handleFilterChange("validado", e.target.value)
                  }
                >
                  <option value="1">Todos</option>
                  <option value="2">Sí</option>
                  <option value="0">No</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Ordenar Por</Form.Label>
                <Form.Select
                  value={filters.orderBy}
                  onChange={(e) =>
                    handleFilterChange("orderBy", e.target.value)
                  }
                >
                  {orderByOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Fecha Desde</Form.Label>
                <Form.Control
                  type="date"
                  value={dateInputs.fecha_desde}
                  onChange={(e) => {
                    const value = e.target.value;
                    setDateInputs((prev) => ({ ...prev, fecha_desde: value }));
                    if (value && value.length === 10) {
                      // Solo si la fecha está completa (YYYY-MM-DD)
                      debouncedDateChange("fecha_desde", value);
                    }
                  }}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Fecha Hasta</Form.Label>
                <Form.Control
                  type="date"
                  value={dateInputs.fecha_hasta}
                  onChange={(e) => {
                    const value = e.target.value;
                    setDateInputs((prev) => ({ ...prev, fecha_hasta: value }));
                    if (value && value.length === 10) {
                      // Solo si la fecha está completa (YYYY-MM-DD)
                      debouncedDateChange("fecha_hasta", value);
                    }
                  }}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Lista de Jornadas */}
      <Card>
        <Card.Header className="d-flex flex-wrap align-items-center">
          <div className="col-12 col-md-auto mb-2 mb-md-0">
            <h5 className="mb-0">Lista de Jornadas</h5>
          </div>
          <div className="col-12 col-md-auto d-flex flex-column flex-md-row gap-2 gap-md-2 ms-md-auto">
            <Button
              variant="outline-secondary"
              className="mb-2 mb-md-0"
              onClick={loadData}
              disabled={tableLoading}
              style={{ width: "100%" }}
              title="Refrescar tabla"
            >
              <FaSync /> Refrescar
            </Button>
            {selectedJornadas.size > 0 && (
              <Button
                variant="success"
                onClick={() => setShowConfirmModal(true)}
                disabled={tableLoading}
                style={{ width: "100%" }}
              >
                <FaCheck /> Validar Seleccionados ({selectedJornadas.size})
              </Button>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          {/* Vista de tabla para desktop */}
          <div className="d-none d-md-block">
            <Table responsive striped hover>
              <thead>
                <tr>
                  <th>
                    <Form.Check
                      type="checkbox"
                      checked={
                        jornadas.filter((j) => canValidateJornada(j)).length > 0 &&
                        jornadas
                          .filter((j) => canValidateJornada(j))
                          .every((j) => selectedJornadas.has(buildJornadaKey(j)))
                      }
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      disabled={tableLoading}
                    />
                  </th>
                  <th>Trabajador</th>
                  <th>Fecha</th>
                  <th>Validado</th>
                  <th>Fecha Validación</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {tableLoading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-4">
                      <div
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      >
                        <span className="visually-hidden">Cargando...</span>
                      </div>
                      Cargando jornadas...
                    </td>
                  </tr>
                ) : jornadas.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-4">
                      No se encontraron jornadas
                    </td>
                  </tr>
                ) : (
                  jornadas.map((jornada) => (
                    <tr
                      key={jornada.VALIDACION_JORNADA_ID || jornada.EMPLEADO_ID + jornada.FECHA}
                      className={getRowClassName(jornada)}
                      onClick={() => handleRowClick(jornada)}
                    >
                      <td>
                        {canValidateJornada(jornada) && (
                          <Form.Check
                            type="checkbox"
                            checked={selectedJornadas.has(buildJornadaKey(jornada))}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleSelectJornada(jornada, e.target.checked);
                            }}
                            disabled={tableLoading}
                          />
                        )}
                      </td>
                      <td>{jornada.EMPLEADO}</td>
                      <td>{formatDate(jornada.FECHA)}</td>
                      <td>{getStatusBadge(jornada.VALIDADO, jornada.HAY_EVENTO)}</td>
                      <td>
                        {jornada.FECHA_VALIDACION
                          ? formatDateTime(jornada.FECHA_VALIDACION)
                          : "-"}
                      </td>
                      <td>
                         <Button
                            variant="outline-info"
                            size="sm"
                            className="me-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleVerFichajes(jornada);
                            }}
                            title="Ver Fichajes"
                          >
                            <FaEye />
                          </Button>
                        {user && canValidateJornada(jornada) && (
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleValidarJornada(jornada);
                            }}
                            title="Validar Jornada"
                            disabled={tableLoading}
                          >
                            <FaCheck />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {/* Vista de cards para móvil */}
          <div className="d-md-none">
            {tableLoading ? (
              <div className="text-center py-4">
                <div
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                >
                  <span className="visually-hidden">Cargando...</span>
                </div>
                Cargando jornadas...
              </div>
            ) : jornadas.length === 0 ? (
              <div className="text-center py-4">No se encontraron jornadas</div>
            ) : (
              <div className="jornadas-cards">
                {jornadas.map((jornada) => (
                  <Card
                    key={jornada.VALIDACION_JORNADA_ID || jornada.EMPLEADO_ID + jornada.FECHA}
                    className={`mb-3 gasto-card ${getRowClassName(jornada)}`}
                    onClick={() => handleRowClick(jornada)}
                  >
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">{formatDate(jornada.FECHA)}</h6>
                          <small className="text-muted">
                            {jornada.EMPLEADO}
                          </small>
                        </div>
                        <div className="d-flex align-items-center">
                          {canValidateJornada(jornada) && (
                            <Form.Check
                              type="checkbox"
                              checked={selectedJornadas.has(buildJornadaKey(jornada))}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleSelectJornada(jornada, e.target.checked);
                              }}
                              disabled={tableLoading}
                              className="me-2"
                            />
                          )}
                          {getStatusBadge(jornada.VALIDADO, jornada.HAY_EVENTO)}
                        </div>
                      </div>

                      <div className="row g-2 mb-2">
                        <div className="col-12">
                          <small className="text-muted d-block">
                            Fecha Validación:
                          </small>
                          <span>
                            {jornada.FECHA_VALIDACION
                              ? formatDateTime(jornada.FECHA_VALIDACION)
                              : "-"}
                          </span>
                        </div>
                      </div>

                      <div className="d-flex flex-wrap gap-1">
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleVerFichajes(jornada);
                            }}
                            title="Ver Fichajes"
                          >
                            <FaEye />
                          </Button>
                        {user && canValidateJornada(jornada) && (
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleValidarJornada(jornada);
                            }}
                            title="Validar Jornada"
                            disabled={tableLoading}
                          >
                            <FaCheck />
                          </Button>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Paginación */}
          {pagination.pages > 1 && (
            <div className="d-flex justify-content-center mt-3">
              <Pagination>
                <Pagination.First
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, page: 1 }))
                  }
                  disabled={pagination.page === 1}
                />
                <Pagination.Prev
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, page: prev.page - 1 }))
                  }
                  disabled={pagination.page === 1}
                />
                {/* Páginas dinámicas centradas en la actual */}
                {Array.from({ length: pagination.pages }, (_, i) => i + 1)
                  .filter(
                    (pageNum) =>
                      pageNum === 1 ||
                      pageNum === pagination.pages ||
                      (pageNum >= pagination.page - 2 &&
                        pageNum <= pagination.page + 2)
                  )
                  .map((pageNum, idx, arr) => {
                    // Añadir puntos suspensivos si hay salto
                    if (idx > 0 && pageNum - arr[idx - 1] > 1) {
                      return [
                        <Pagination.Ellipsis
                          key={`ellipsis-${pageNum}`}
                          disabled
                        />,
                        <Pagination.Item
                          key={pageNum}
                          active={pageNum === pagination.page}
                          onClick={() =>
                            setPagination((prev) => ({
                              ...prev,
                              page: pageNum,
                            }))
                          }
                        >
                          {pageNum}
                        </Pagination.Item>,
                      ];
                    }
                    return (
                      <Pagination.Item
                        key={pageNum}
                        active={pageNum === pagination.page}
                        onClick={() =>
                          setPagination((prev) => ({ ...prev, page: pageNum }))
                        }
                      >
                        {pageNum}
                      </Pagination.Item>
                    );
                  })}
                <Pagination.Next
                  onClick={() =>
                    setPagination((prev) => ({
                      ...prev,
                      page: Math.min(pagination.pages, prev.page + 1),
                    }))
                  }
                  disabled={pagination.page === pagination.pages}
                />
                <Pagination.Last
                  onClick={() =>
                    setPagination((prev) => ({
                      ...prev,
                      page: pagination.pages,
                    }))
                  }
                  disabled={pagination.page === pagination.pages}
                />
              </Pagination>
            </div>
          )}
        </Card.Body>
      </Card>
      {/* Modal de confirmación para validación masiva */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Validación Masiva</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <div className="mb-3">
              <FaCheck className="text-success" style={{ fontSize: "3rem" }} />
            </div>
            <h5>¿Estás seguro?</h5>
            <p>
              Se van a validar <strong>{selectedJornadas.size} jornadas</strong> seleccionadas.
            </p>
            <p className="text-danger">
              <strong>Esta acción no se podrá revertir.</strong>
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            Cancelar
          </Button>
          <Button variant="success" onClick={handleValidarSeleccionados} disabled={tableLoading}>
            {tableLoading ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visualmente-oculto">Validando...</span>
                </div>
                Validando...
              </>
            ) : (
              <>
                <FaCheck /> Confirmar Validación
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de confirmación para validación individual */}
      <Modal
        show={showConfirmIndividualModal}
        onHide={() => setShowConfirmIndividualModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Validación Individual</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <div className="mb-3">
              <FaCheck className="text-success" style={{ fontSize: "3rem" }} />
            </div>
            <h5>¿Estás seguro?</h5>
            <p>Se va a validar la jornada seleccionada.</p>
            <p className="text-danger">
              <strong>Esta acción no se podrá revertir.</strong>
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirmIndividualModal(false)}
          >
            Cancelar
          </Button>
          <Button
            variant="success"
            onClick={() => handleValidarJornadaIndividual(jornadaToValidate)}
            disabled={tableLoading}
          >
            {tableLoading ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visualmente-oculto">Validando...</span>
                </div>
                Validando...
              </>
            ) : (
              <>
                <FaCheck /> Confirmar Validación
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Fichajes */}
      <Modal
        show={selectedJornadaShifts.show}
        onHide={() =>
          setSelectedJornadaShifts({ jornada: null, loading: false, shifts: [], show: false, error: null })
        }
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Fichajes del día</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedJornadaShifts.loading ? (
            <div className="text-center py-3">
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              Cargando turnos...
            </div>
          ) : selectedJornadaShifts.error ? (
            <div className="text-danger">{selectedJornadaShifts.error}</div>
          ) : selectedJornadaShifts.shifts.length === 0 ? (
            <div>No hay turnos para este día.</div>
          ) : (
            <div>
              {selectedJornadaShifts.shifts.map((shift, idx) => (
                <div key={shift.id || idx} className="mb-2">
                  <strong>Turno {idx + 1}:</strong> {shift.clock_in} - {shift.clock_out || "--:--"}
                  {shift.observations ? (
                    <div className="text-muted small">{shift.observations}</div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() =>
              setSelectedJornadaShifts({ jornada: null, loading: false, shifts: [], show: false, error: null })
            }
          >
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default JornadaList;
